import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { adminApi } from '../services/api';
import { formatTournamentDateTime } from '../utils/dateTime';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import './AdminPage.css';

const GAME_TYPES = ['Bo3', 'Bo1 Swiss Bo3 Top Cut', 'Bo1'];
const TOP_CUT_OPTIONS = ['4', '8', '16', '32', '2 défaites ou moins'];

export default function AdminPage() {
  const { user, token, loading: authLoading, login, logout } = useAuth();

  if (authLoading) {
    return <Loading text="Vérification de l'authentification..." />;
  }

  if (!user) {
    return <AdminLogin onLogin={login} />;
  }

  if (user.email !== 'matthieu.barbe01@gmail.com') {
    return (
      <div className="admin-login">
        <div className="admin-login__card card">
          <h1 className="admin-login__title staatliches-regular">Accès Refusé</h1>
          <p className="admin-login__text chiron-goround-tc-body">
            Ce compte n'est pas autorisé à accéder à l'administration.
          </p>
          <Button variant="secondary" size="lg" onClick={logout}>
            Déconnexion
          </Button>
        </div>
      </div>
    );
  }

  return <AdminDashboard user={user} token={token} onLogout={logout} />;
}

function AdminLogin({ onLogin }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      await onLogin();
    } catch (err) {
      setError('Échec de la connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login">
      <div className="admin-login__card card">
        <h1 className="admin-login__title staatliches-regular">Panneau d'Administration</h1>
        <p className="admin-login__text chiron-goround-tc-body">
          Connectez-vous avec votre compte Google pour accéder à l'administration.
        </p>
        {error && <p className="admin-login__error">{error}</p>}
        <Button
          variant="dark"
          size="lg"
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? 'Connexion...' : 'Connexion avec Google'}
        </Button>
      </div>
    </div>
  );
}

function AdminDashboard({ user, token, onLogout }) {
  const [message, setMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Tournaments
  const [tournaments, setTournaments] = useState(null);
  const [tournamentsLoading, setTournamentsLoading] = useState(true);

  // Non-circuit tournaments
  const [ncTournaments, setNcTournaments] = useState(null);
  const [ncTournamentsLoading, setNcTournamentsLoading] = useState(true);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [showNcModal, setShowNcModal] = useState(false);
  const [showPalmaresModal, setShowPalmaresModal] = useState(false);

  // Palmarès
  const [palmaresEntries, setPalmaresEntries] = useState(null);
  const [palmaresLoading, setPalmaresLoading] = useState(true);

  // Invitational
  const [invitational, setInvitational] = useState({
    autoQualifyWinners: true,
    qualificationType: 'points',
    threshold: 200,
    dateFrom: '',
    dateTo: '',
  });
  const [invitationalLoading, setInvitationalLoading] = useState(true);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadTournaments = useCallback(async () => {
    try {
      setTournamentsLoading(true);
      const result = await adminApi.getAllTournaments(token);
      setTournaments(result);
    } catch (err) {
      showMessage(err.message, 'error');
    } finally {
      setTournamentsLoading(false);
    }
  }, [token]);

  const loadNcTournaments = useCallback(async () => {
    try {
      setNcTournamentsLoading(true);
      const result = await adminApi.getAllNonCircuitTournaments(token);
      setNcTournaments(result);
    } catch (err) {
      showMessage(err.message, 'error');
    } finally {
      setNcTournamentsLoading(false);
    }
  }, [token]);

  const loadPalmares = useCallback(async () => {
    try {
      setPalmaresLoading(true);
      const { getPalmares } = await import('../services/api').then(m => ({ getPalmares: m.api.getPalmares }));
      const result = await getPalmares();
      setPalmaresEntries(result);
    } catch (err) {
      showMessage(err.message, 'error');
    } finally {
      setPalmaresLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTournaments();
    loadNcTournaments();
    loadPalmares();
    adminApi.getInvitationalSettings(token)
      .then((settings) => setInvitational({
        autoQualifyWinners: settings.autoQualifyWinners ?? true,
        qualificationType: settings.qualificationType ?? 'points',
        threshold: settings.threshold ?? 200,
        dateFrom: settings.dateFrom ?? '',
        dateTo: settings.dateTo ?? '',
      }))
      .catch(() => {})
      .finally(() => setInvitationalLoading(false));
  }, [loadTournaments, token]);

  const handleDeleteTournament = async (id, name) => {
    if (!window.confirm(`Supprimer le tournoi "${name}" ?`)) return;
    try {
      setActionLoading(true);
      const result = await adminApi.deleteTournament(token, id);
      showMessage(result.message);
      loadTournaments();
    } catch (err) {
      showMessage(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteNcTournament = async (id, name) => {
    if (!window.confirm(`Supprimer le tournoi "${name}" ?`)) return;
    try {
      setActionLoading(true);
      const result = await adminApi.deleteNonCircuitTournament(token, id);
      showMessage(result.message);
      loadNcTournaments();
    } catch (err) {
      showMessage(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePalmares = async (id, player) => {
    if (!window.confirm(`Supprimer l'entrée de "${player}" ?`)) return;
    try {
      setActionLoading(true);
      const result = await adminApi.deletePalmaresEntry(token, id);
      showMessage(result.message);
      loadPalmares();
    } catch (err) {
      showMessage(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRecalculateRankings = async () => {
    try {
      setActionLoading(true);
      const result = await adminApi.recalculateRankings(token);
      showMessage(result.message);
    } catch (err) {
      showMessage(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefreshAnalytics = async () => {
    try {
      setActionLoading(true);
      const result = await adminApi.refreshAnalytics(token);
      showMessage(result.message);
    } catch (err) {
      showMessage(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClearCache = async () => {
    try {
      const result = await adminApi.clearCache(token);
      showMessage(result.message);
    } catch (err) {
      showMessage(err.message, 'error');
    }
  };

  const handleSaveInvitational = async () => {
    try {
      setActionLoading(true);
      const result = await adminApi.updateInvitationalSettings(token, {
        ...invitational,
        threshold: Number(invitational.threshold) || 1,
      });
      showMessage(result.message);
    } catch (err) {
      showMessage(err.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTournamentCreated = (result) => {
    showMessage(result.message);
    setShowModal(false);
    loadTournaments();
  };

  const handleNcTournamentCreated = (result) => {
    showMessage(result.message);
    setShowNcModal(false);
    loadNcTournaments();
  };

  const handlePalmaresCreated = (result) => {
    showMessage(result.message);
    setShowPalmaresModal(false);
    loadPalmares();
  };

  return (
    <div className="admin-dashboard">
      <header className="admin-dashboard__header section section--dark">
        <div className="container admin-dashboard__header-inner">
          <div>
            <h1 className="admin-dashboard__title staatliches-regular">
              Administration
            </h1>
            <p className="admin-dashboard__user chiron-goround-tc-light">
              Connecté en tant que {user.displayName || user.email}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={onLogout}>
            Déconnexion
          </Button>
        </div>
      </header>

      {message && (
        <div className={`admin-dashboard__message admin-dashboard__message--${message.type}`}>
          <div className="container chiron-goround-tc-body">{message.text}</div>
        </div>
      )}

      {/* Quick Actions */}
      <section className="admin-dashboard__actions section">
        <div className="container">
          <h2 className="admin-dashboard__section-title staatliches-regular">Actions Rapides</h2>
          <div className="admin-dashboard__actions-grid">
            <Button variant="secondary" onClick={handleRecalculateRankings} disabled={actionLoading}>
              Recalculer le Classement
            </Button>
            <Button variant="dark" onClick={handleRefreshAnalytics} disabled={actionLoading}>
              Rafraîchir les Stats
            </Button>
            <Button variant="outline" onClick={handleClearCache} disabled={actionLoading}>
              Vider le Cache
            </Button>
          </div>
        </div>
      </section>

      {/* Tournaments */}
      <section className="admin-dashboard__tournaments section section--alt">
        <div className="container">
          <div className="admin-tournaments__header">
            <h2 className="admin-dashboard__section-title staatliches-regular">
              Tournois Champions Collective
            </h2>
            <Button onClick={() => setShowModal(true)} disabled={actionLoading}>
              + Ajouter un Tournoi
            </Button>
          </div>

          {tournamentsLoading && <Loading text="Chargement des tournois..." />}

          {!tournamentsLoading && tournaments && tournaments.length > 0 && (
            <div className="admin-tournaments-list">
              {tournaments.map((t) => (
                <div key={t.id} className="admin-tournament-item card">
                  <div className="admin-tournament-item__info">
                    <h3 className="admin-tournament-item__name staatliches-regular">{t.name}</h3>
                    <div className="admin-tournament-item__meta chiron-goround-tc-light">
                      {t.date && (
                        <span>
                          {formatTournamentDateTime(t.date, t.time, { style: 'short' })}
                        </span>
                      )}
                      <span>{t.format}</span>
                      <span>{t.gameType}</span>
                      <span>Top Cut : {t.topCut}</span>
                      {t.maxPlayers && <span>Max : {t.maxPlayers} joueurs</span>}
                    </div>
                    {t.limitlessUrl && (
                      <a
                        href={t.limitlessUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="admin-tournament-item__link chiron-goround-tc-light"
                      >
                        Voir sur Limitless →
                      </a>
                    )}
                  </div>
                  <div className="admin-tournament-item__controls">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTournament(t.id, t.name)}
                      disabled={actionLoading}
                    >
                      Supprimer
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!tournamentsLoading && (!tournaments || tournaments.length === 0) && (
            <p className="chiron-goround-tc-body" style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Aucun tournoi pour le moment. Cliquez sur « + Ajouter un Tournoi » pour commencer.
            </p>
          )}
        </div>
      </section>

      {/* Invitational Settings */}
      <section className="admin-dashboard__invitational section">
        <div className="container">
          <h2 className="admin-dashboard__section-title staatliches-regular">
            Paramètres de l'Invitational
          </h2>

          {invitationalLoading ? (
            <Loading text="Chargement des paramètres..." />
          ) : (
            <div className="admin-invitational card">
              <div className="admin-invitational__field">
                <label className="admin-invitational__label chiron-goround-tc-body">
                  <input
                    type="checkbox"
                    checked={invitational.autoQualifyWinners}
                    onChange={(e) =>
                      setInvitational({ ...invitational, autoQualifyWinners: e.target.checked })
                    }
                  />
                  Qualifier automatiquement les vainqueurs de tournoi
                </label>
              </div>

              <div className="admin-invitational__field">
                <label className="admin-invitational__label chiron-goround-tc-body">
                  Mode de qualification
                </label>
                <select
                  className="admin-invitational__select chiron-goround-tc-body"
                  value={invitational.qualificationType}
                  onChange={(e) =>
                    setInvitational({ ...invitational, qualificationType: e.target.value })
                  }
                >
                  <option value="points">Par seuil de points</option>
                  <option value="rank">Par classement (top N)</option>
                </select>
              </div>

              <div className="admin-invitational__field">
                <label className="admin-invitational__label chiron-goround-tc-body">
                  {invitational.qualificationType === 'points'
                    ? 'Seuil de points minimum'
                    : 'Nombre de joueurs qualifiés (top N)'}
                </label>
                <input
                  type="number"
                  className="admin-invitational__input chiron-goround-tc-body"
                  value={invitational.threshold}
                  min="1"
                  onChange={(e) =>
                    setInvitational({ ...invitational, threshold: parseInt(e.target.value, 10) || 1 })
                  }
                />
              </div>

              <div className="admin-invitational__field">
                <label className="admin-invitational__label chiron-goround-tc-body">
                  Période de comptage des points
                </label>
                <div className="admin-invitational__date-row">
                  <div>
                    <label className="chiron-goround-tc-light" style={{ fontSize: '0.8rem' }}>Du</label>
                    <input
                      type="date"
                      className="admin-invitational__input chiron-goround-tc-body"
                      value={invitational.dateFrom || ''}
                      onChange={(e) =>
                        setInvitational({ ...invitational, dateFrom: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="chiron-goround-tc-light" style={{ fontSize: '0.8rem' }}>Au</label>
                    <input
                      type="date"
                      className="admin-invitational__input chiron-goround-tc-body"
                      value={invitational.dateTo || ''}
                      onChange={(e) =>
                        setInvitational({ ...invitational, dateTo: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveInvitational} disabled={actionLoading}>
                Sauvegarder les paramètres
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Add Tournament Modal */}
      {showModal && (
        <AddTournamentModal
          token={token}
          onClose={() => setShowModal(false)}
          onCreated={handleTournamentCreated}
        />
      )}

      {/* Non-Circuit Tournaments */}
      <section className="admin-dashboard__tournaments section section--alt">
        <div className="container">
          <div className="admin-tournaments__header">
            <h2 className="admin-dashboard__section-title staatliches-regular">
              Tournois Mis en Avant (Hors Circuit)
            </h2>
            <Button onClick={() => setShowNcModal(true)} disabled={actionLoading}>
              + Ajouter un Tournoi Hors Circuit
            </Button>
          </div>

          {ncTournamentsLoading && <Loading text="Chargement des tournois..." />}

          {!ncTournamentsLoading && ncTournaments && ncTournaments.length > 0 && (
            <div className="admin-tournaments-list">
              {ncTournaments.map((t) => (
                <div key={t.id} className="admin-tournament-item card">
                  <div className="admin-tournament-item__info">
                    <h3 className="admin-tournament-item__name staatliches-regular">{t.name}</h3>
                    <div className="admin-tournament-item__meta chiron-goround-tc-light">
                      {t.date && (
                        <span>
                          {formatTournamentDateTime(t.date, t.time, { style: 'short' })}
                        </span>
                      )}
                      <span>{t.format}</span>
                      <span>{t.gameType}</span>
                      <span>Top Cut : {t.topCut}</span>
                      {t.maxPlayers && <span>Max : {t.maxPlayers} joueurs</span>}
                    </div>
                    {t.limitlessUrl && (
                      <a
                        href={t.limitlessUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="admin-tournament-item__link chiron-goround-tc-light"
                      >
                        Voir sur Limitless →
                      </a>
                    )}
                  </div>
                  <div className="admin-tournament-item__controls">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteNcTournament(t.id, t.name)}
                      disabled={actionLoading}
                    >
                      Supprimer
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!ncTournamentsLoading && (!ncTournaments || ncTournaments.length === 0) && (
            <p className="chiron-goround-tc-body" style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Aucun tournoi hors circuit pour le moment.
            </p>
          )}
        </div>
      </section>

      {/* Add Non-Circuit Tournament Modal */}
      {showNcModal && (
        <AddNonCircuitTournamentModal
          token={token}
          onClose={() => setShowNcModal(false)}
          onCreated={handleNcTournamentCreated}
        />
      )}

      {/* Palmarès */}
      <section className="admin-dashboard__palmares section">
        <div className="container">
          <div className="admin-tournaments__header">
            <h2 className="admin-dashboard__section-title staatliches-regular">
              Palmarès
            </h2>
            <Button onClick={() => setShowPalmaresModal(true)} disabled={actionLoading}>
              + Ajouter une Entrée
            </Button>
          </div>

          {palmaresLoading && <Loading text="Chargement du palmarès..." />}

          {!palmaresLoading && palmaresEntries && palmaresEntries.length > 0 && (
            <div className="admin-tournaments-list">
              {palmaresEntries.map((entry) => (
                <div key={entry.id} className="admin-tournament-item card">
                  <div className="admin-tournament-item__info">
                    <h3 className="admin-tournament-item__name staatliches-regular">
                      {entry.tournamentName} - {entry.player}
                    </h3>
                    <div className="admin-tournament-item__meta chiron-goround-tc-light">
                      <span>{entry.date}</span>
                      <span>Équipe : {entry.team?.join(', ')}</span>
                    </div>
                  </div>
                  <div className="admin-tournament-item__controls">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeletePalmares(entry.id, entry.player)}
                      disabled={actionLoading}
                    >
                      Supprimer
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!palmaresLoading && (!palmaresEntries || palmaresEntries.length === 0) && (
            <p className="chiron-goround-tc-body" style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Aucune entrée dans le palmarès pour le moment.
            </p>
          )}
        </div>
      </section>

      {/* Add Palmarès Modal */}
      {showPalmaresModal && (
        <AddPalmaresModal
          token={token}
          onClose={() => setShowPalmaresModal(false)}
          onCreated={handlePalmaresCreated}
        />
      )}
    </div>
  );
}

function AddTournamentModal({ token, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    limitlessUrl: '',
    date: '',
    time: '',
    maxPlayers: '',
    format: 'M-B',
    gameType: 'Bo3',
    topCut: '8',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Le nom du tournoi est requis');
      return;
    }
    if (!form.date) {
      setError('La date est requise');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await adminApi.createTournament(token, {
        name: form.name.trim(),
        limitlessUrl: form.limitlessUrl.trim() || null,
        date: form.date,
        time: form.time || null,
        maxPlayers: form.maxPlayers ? Number(form.maxPlayers) : null,
        format: form.format,
        gameType: form.gameType,
        topCut: form.topCut,
      });
      onCreated(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h2 className="admin-modal__title staatliches-regular">Ajouter un Tournoi</h2>
          <button className="admin-modal__close" onClick={onClose} aria-label="Fermer">×</button>
        </div>

        <form className="admin-modal__form" onSubmit={handleSubmit}>
          {error && <p className="admin-modal__error chiron-goround-tc-body">{error}</p>}

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Nom du tournoi *</label>
            <input
              type="text"
              className="admin-invitational__input chiron-goround-tc-body"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Ex: Champions Cup #1"
              autoFocus
            />
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Lien page Limitless</label>
            <input
              type="url"
              className="admin-invitational__input chiron-goround-tc-body"
              value={form.limitlessUrl}
              onChange={(e) => updateField('limitlessUrl', e.target.value)}
              placeholder="https://play.limitlesstcg.com/tournament/..."
            />
          </div>

          <div className="admin-modal__row">
            <div className="admin-modal__field">
              <label className="admin-modal__label chiron-goround-tc-body">Date de début *</label>
              <input
                type="date"
                className="admin-invitational__input chiron-goround-tc-body"
                value={form.date}
                onChange={(e) => updateField('date', e.target.value)}
              />
            </div>
            <div className="admin-modal__field">
              <label className="admin-modal__label chiron-goround-tc-body">Heure de début</label>
              <input
                type="time"
                className="admin-invitational__input chiron-goround-tc-body"
                value={form.time}
                onChange={(e) => updateField('time', e.target.value)}
              />
            </div>
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">
              Nombre maximum de joueurs <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>(optionnel)</span>
            </label>
            <input
              type="number"
              className="admin-invitational__input chiron-goround-tc-body"
              value={form.maxPlayers}
              onChange={(e) => updateField('maxPlayers', e.target.value)}
              min="1"
              placeholder="Ex: 64"
            />
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Format</label>
            <select
              className="admin-invitational__select chiron-goround-tc-body"
              value={form.format}
              onChange={(e) => updateField('format', e.target.value)}
            >
              <option value="M-A">M-A</option>
            </select>
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Type de match</label>
            <select
              className="admin-invitational__select chiron-goround-tc-body"
              value={form.gameType}
              onChange={(e) => updateField('gameType', e.target.value)}
            >
              {GAME_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Type de top cut</label>
            <select
              className="admin-invitational__select chiron-goround-tc-body"
              value={form.topCut}
              onChange={(e) => updateField('topCut', e.target.value)}
            >
              {TOP_CUT_OPTIONS.map(opt => (
                <option key={opt} value={opt}>Top {opt}</option>
              ))}
            </select>
          </div>

          <div className="admin-modal__actions">
            <Button variant="outline" type="button" onClick={onClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Création...' : 'Créer le tournoi'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddNonCircuitTournamentModal({ token, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    limitlessUrl: '',
    date: '',
    time: '',
    maxPlayers: '',
    format: 'M-A',
    gameType: 'Bo3',
    topCut: '8',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Le nom du tournoi est requis');
      return;
    }
    if (!form.date) {
      setError('La date est requise');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await adminApi.createNonCircuitTournament(token, {
        name: form.name.trim(),
        limitlessUrl: form.limitlessUrl.trim() || null,
        date: form.date,
        time: form.time || null,
        maxPlayers: form.maxPlayers ? Number(form.maxPlayers) : null,
        format: form.format,
        gameType: form.gameType,
        topCut: form.topCut,
      });
      onCreated(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h2 className="admin-modal__title staatliches-regular">Ajouter un Tournoi Hors Circuit</h2>
          <button className="admin-modal__close" onClick={onClose} aria-label="Fermer">×</button>
        </div>

        <form className="admin-modal__form" onSubmit={handleSubmit}>
          {error && <p className="admin-modal__error chiron-goround-tc-body">{error}</p>}

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Nom du tournoi *</label>
            <input
              type="text"
              className="admin-invitational__input chiron-goround-tc-body"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Ex: Community Cup #1"
              autoFocus
            />
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Lien page Limitless</label>
            <input
              type="url"
              className="admin-invitational__input chiron-goround-tc-body"
              value={form.limitlessUrl}
              onChange={(e) => updateField('limitlessUrl', e.target.value)}
              placeholder="https://play.limitlesstcg.com/tournament/..."
            />
          </div>

          <div className="admin-modal__row">
            <div className="admin-modal__field">
              <label className="admin-modal__label chiron-goround-tc-body">Date de début *</label>
              <input
                type="date"
                className="admin-invitational__input chiron-goround-tc-body"
                value={form.date}
                onChange={(e) => updateField('date', e.target.value)}
              />
            </div>
            <div className="admin-modal__field">
              <label className="admin-modal__label chiron-goround-tc-body">Heure de début</label>
              <input
                type="time"
                className="admin-invitational__input chiron-goround-tc-body"
                value={form.time}
                onChange={(e) => updateField('time', e.target.value)}
              />
            </div>
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">
              Nombre maximum de joueurs <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>(optionnel)</span>
            </label>
            <input
              type="number"
              className="admin-invitational__input chiron-goround-tc-body"
              value={form.maxPlayers}
              onChange={(e) => updateField('maxPlayers', e.target.value)}
              min="1"
              placeholder="Ex: 64"
            />
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Format</label>
            <select
              className="admin-invitational__select chiron-goround-tc-body"
              value={form.format}
              onChange={(e) => updateField('format', e.target.value)}
            >
              <option value="M-A">M-A</option>
            </select>
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Type de match</label>
            <select
              className="admin-invitational__select chiron-goround-tc-body"
              value={form.gameType}
              onChange={(e) => updateField('gameType', e.target.value)}
            >
              {GAME_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Type de top cut</label>
            <select
              className="admin-invitational__select chiron-goround-tc-body"
              value={form.topCut}
              onChange={(e) => updateField('topCut', e.target.value)}
            >
              {TOP_CUT_OPTIONS.map(opt => (
                <option key={opt} value={opt}>Top {opt}</option>
              ))}
            </select>
          </div>

          <div className="admin-modal__actions">
            <Button variant="outline" type="button" onClick={onClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Création...' : 'Créer le tournoi'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddPalmaresModal({ token, onClose, onCreated }) {
  const [form, setForm] = useState({
    player: '',
    tournamentName: '',
    date: '',
    team: ['', '', '', '', '', ''],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updateTeamMember = (index, value) => {
    setForm(prev => {
      const newTeam = [...prev.team];
      newTeam[index] = value;
      return { ...prev, team: newTeam };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.player.trim()) {
      setError('Le nom du joueur est requis');
      return;
    }
    if (!form.tournamentName.trim()) {
      setError('Le nom du tournoi est requis');
      return;
    }
    if (!form.date) {
      setError('La date est requise');
      return;
    }
    if (form.team.some(p => !p.trim())) {
      setError('Les 6 Pokémon de l\'équipe sont requis');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await adminApi.createPalmaresEntry(token, {
        player: form.player.trim(),
        tournamentName: form.tournamentName.trim(),
        date: form.date,
        team: form.team.map(p => p.trim().toLowerCase()),
      });
      onCreated(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h2 className="admin-modal__title staatliches-regular">Ajouter au Palmarès</h2>
          <button className="admin-modal__close" onClick={onClose} aria-label="Fermer">×</button>
        </div>

        <form className="admin-modal__form" onSubmit={handleSubmit}>
          {error && <p className="admin-modal__error chiron-goround-tc-body">{error}</p>}

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Nom du joueur *</label>
            <input
              type="text"
              className="admin-invitational__input chiron-goround-tc-body"
              value={form.player}
              onChange={(e) => updateField('player', e.target.value)}
              placeholder="Ex: Pseudo du joueur"
              autoFocus
            />
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Nom du tournoi *</label>
            <input
              type="text"
              className="admin-invitational__input chiron-goround-tc-body"
              value={form.tournamentName}
              onChange={(e) => updateField('tournamentName', e.target.value)}
              placeholder="Ex: Invitational #1"
            />
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">Date *</label>
            <input
              type="date"
              className="admin-invitational__input chiron-goround-tc-body"
              value={form.date}
              onChange={(e) => updateField('date', e.target.value)}
            />
          </div>

          <div className="admin-modal__field">
            <label className="admin-modal__label chiron-goround-tc-body">
              Équipe (6 Pokémon) * <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>ID Pokémon (ex: pikachu, charizard)</span>
            </label>
            <div className="admin-palmares__team-grid">
              {form.team.map((pokemon, idx) => (
                <div key={idx} className="admin-palmares__team-member">
                  <input
                    type="text"
                    className="admin-invitational__input chiron-goround-tc-body"
                    value={pokemon}
                    onChange={(e) => updateTeamMember(idx, e.target.value)}
                    placeholder={`Pokémon ${idx + 1}`}
                  />
                  {pokemon.trim() && (
                    <img
                      src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemon.trim().toLowerCase()}.png`}
                      alt={pokemon}
                      className="admin-palmares__sprite-preview"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="admin-modal__actions">
            <Button variant="outline" type="button" onClick={onClose} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Création...' : 'Ajouter au palmarès'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
